import { ParticleSystem } from '../ui/particles';
import type { HumNote, CanvasTransform, ThemePreset } from '../types';
import { drawShape } from './palette';
import { getCurrentTheme } from '../themes';
import { NoiseOverlay, WavesBackground, ClickSparkSystem, ShockwaveSystem } from './effects';

let canvas: HTMLCanvasElement;
let ctx: CanvasRenderingContext2D;
let particles: ParticleSystem;
let animFrameId = 0;
let notes: HumNote[] = [];
let transform: CanvasTransform = { x: 0, y: 0, scale: 1 };
let playbackGlowIndex = -1;
let gradientAngle = 0;
let audioAmplitude = 0;
let symmetryMode: 1 | 2 | 4 = 1;
const noiseOverlay = new NoiseOverlay();
const wavesBg = new WavesBackground();
const clickSparks = new ClickSparkSystem();
const shockwaves = new ShockwaveSystem();

let hoveredNote: HumNote | null = null;

export function setSymmetryMode(m: 1 | 2 | 4): void {
  symmetryMode = m;
}

export function getSymmetryMode(): 1 | 2 | 4 {
  return symmetryMode;
}

export function setHoveredNote(note: HumNote | null): void {
  hoveredNote = note;
}

export function getShockwaves(): ShockwaveSystem {
  return shockwaves;
}

const CANVAS_BORDER = 2;

export function initRenderer(canvasEl: HTMLCanvasElement): void {
  canvas = canvasEl;
  ctx = canvas.getContext('2d')!;
  resizeCanvas();
  window.addEventListener('resize', resizeCanvas);
}

export function resizeCanvas(): void {
  const dpr = window.devicePixelRatio || 1;
  const w = window.innerWidth;
  const h = window.innerHeight;
  canvas.width = w * dpr;
  canvas.height = h * dpr;
  canvas.style.width = w + 'px';
  canvas.style.height = h + 'px';
  ctx.scale(dpr, dpr);
  if (particles) {
    particles.resize(w, h);
  }
}

export function getTransform(): CanvasTransform {
  return transform;
}

export function setTransform(t: CanvasTransform): void {
  transform = t;
}

export function initParticles(): void {
  const w = window.innerWidth;
  const h = window.innerHeight;
  particles = new ParticleSystem(w, h);
}

export function getParticleSystem(): ParticleSystem {
  return particles;
}

export function setNotes(n: HumNote[]): void {
  notes = n;
}

export function setPlaybackGlow(index: number): void {
  playbackGlowIndex = index;
}

export function setAudioAmplitude(amp: number): void {
  audioAmplitude = amp;
}

export function getClickSparks(): ClickSparkSystem {
  return clickSparks;
}

export function startRenderLoop(): void {
  const render = (now: number) => {
    const dpr = window.devicePixelRatio || 1;
    const w = window.innerWidth;
    const h = window.innerHeight;
    const theme = getCurrentTheme();

    ctx.save();
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    gradientAngle += 0.12;
    drawAnimatedBackground(ctx, w, h, theme, audioAmplitude);

    wavesBg.draw(ctx, w, h, audioAmplitude, theme.accentRgb);
    noiseOverlay.draw(ctx, w, h);

    ctx.save();
    ctx.translate(transform.x, transform.y);
    ctx.scale(transform.scale, transform.scale);

    const drawWorldContent = () => {
      const minX = -transform.x / transform.scale - 350;
      const maxX = (w - transform.x) / transform.scale + 350;
      const minY = -transform.y / transform.scale - 350;
      const maxY = (h - transform.y) / transform.scale + 350;

      for (let i = 0, len = notes.length; i < len; i++) {
        const note = notes[i];
        if (note.x < minX || note.x > maxX || note.y < minY || note.y > maxY) {
          continue;
        }

        const glowIntensity = playbackGlowIndex >= 0 && notes[playbackGlowIndex]?.id === note.id
          ? 2.5
          : 0.8;

        if (note.style && note.style.rotationSpeed !== 0) {
          note.rotation += note.style.rotationSpeed;
        }

        const age = (now - note.birthTime) / 1000;
        let spawnScale = 1;
        if (age < 0.2) {
          spawnScale = age / 0.2;
          spawnScale = 1 - Math.pow(1 - spawnScale, 3);
        }

        if (note.trail.length > 1) {
          const trailLen = note.trail.length;

          // Ultra-thin, clean, elegant single trail line
          ctx.save();
          ctx.globalAlpha = note.alpha * 0.2;
          ctx.strokeStyle = note.color;
          ctx.lineWidth = 0.8;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          ctx.beginPath();
          ctx.moveTo(note.trail[0].x, note.trail[0].y);
          for (let t = 1; t < trailLen; t++) {
            ctx.lineTo(note.trail[t].x, note.trail[t].y);
          }
          ctx.lineTo(note.x, note.y);
          ctx.stroke();
          ctx.restore();
        }

        if (spawnScale < 1) {
          ctx.save();
          ctx.translate(note.x, note.y);
          ctx.scale(spawnScale, spawnScale);
          ctx.translate(-note.x, -note.y);
          drawShape(ctx, note, undefined, glowIntensity * theme.glowIntensity * spawnScale);
          ctx.restore();
        } else {
          drawShape(ctx, note, undefined, glowIntensity * theme.glowIntensity);
        }
      }
    };

    const drawWithSymmetry = () => {
      drawWorldContent();
      if (symmetryMode === 2 || symmetryMode === 4) {
        const cx = (w / 2 - transform.x) / transform.scale;
        const cy = (h / 2 - transform.y) / transform.scale;

        ctx.save();
        ctx.shadowBlur = 0; // Disable shadow blur on reflected symmetry passes for 60 FPS performance

        if (symmetryMode >= 2) {
          ctx.save();
          ctx.translate(2 * cx, 0);
          ctx.scale(-1, 1);
          drawWorldContent();
          ctx.restore();
        }
        if (symmetryMode === 4) {
          ctx.save();
          ctx.translate(0, 2 * cy);
          ctx.scale(1, -1);
          drawWorldContent();
          ctx.restore();

          ctx.save();
          ctx.translate(2 * cx, 2 * cy);
          ctx.scale(-1, -1);
          drawWorldContent();
          ctx.restore();
        }
        ctx.restore();
      }
    };

    drawWithSymmetry();

    if (hoveredNote) {
      drawHoveredNoteTooltip(ctx, hoveredNote);
    }

    ctx.restore();

    drawCanvasBorder(ctx, w, h, theme);

    if (particles) {
      particles.update(audioAmplitude);
      particles.draw(ctx);
    }

    clickSparks.update();
    clickSparks.draw(ctx);

    shockwaves.update();
    shockwaves.draw(ctx);

    ctx.restore();
    animFrameId = requestAnimationFrame(render);
  };
  animFrameId = requestAnimationFrame(render);
}

function drawCanvasBorder(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  theme: ThemePreset,
): void {
  ctx.save();
  ctx.strokeStyle = theme.accent;
  ctx.lineWidth = CANVAS_BORDER;
  ctx.globalAlpha = 0.2;
  ctx.shadowColor = theme.accent;
  ctx.shadowBlur = 4;
  const inset = CANVAS_BORDER * 2;
  ctx.strokeRect(inset, inset, w - inset * 2, h - inset * 2);
  ctx.restore();
}

function drawAnimatedBackground(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  theme: ThemePreset,
  amp: number,
): void {
  const t = gradientAngle * 0.01;
  const pulse = amp * 3;
  const breathe = 1 + pulse * 0.3;

  const grad1 = ctx.createLinearGradient(
    w * 0.5 + Math.sin(t) * w * 0.3 * breathe,
    0,
    w * 0.5 + Math.cos(t * 0.7) * w * 0.3 * breathe,
    h,
  );
  grad1.addColorStop(0, theme.bg);
  grad1.addColorStop(0.35, theme.bgGrad);
  grad1.addColorStop(0.65, theme.bgGrad);
  grad1.addColorStop(1, theme.bg);
  ctx.fillStyle = grad1;
  ctx.fillRect(0, 0, w, h);

  ctx.globalAlpha = 0.06 + Math.sin(t * 0.5) * 0.025 + pulse * 0.12;
  const washR = w * (0.6 + pulse * 0.15);
  const wash = ctx.createRadialGradient(
    w * 0.5 + Math.cos(t * 0.3) * w * 0.2,
    h * 0.5 + Math.sin(t * 0.4) * h * 0.2,
    0,
    w * 0.5,
    h * 0.5,
    washR,
  );
  wash.addColorStop(0, `rgba(${theme.accentRgb}, ${0.3 + pulse * 0.25})`);
  wash.addColorStop(0.5, `rgba(${theme.accentRgb}, ${0.1 + pulse * 0.1})`);
  wash.addColorStop(1, 'transparent');
  ctx.fillStyle = wash;
  ctx.fillRect(0, 0, w, h);
  ctx.globalAlpha = 1;

  if (pulse > 0.05) {
    ctx.save();
    ctx.globalAlpha = pulse * 0.15;
    ctx.strokeStyle = theme.accent;
    ctx.lineWidth = 1;
    const rings = 3;
    for (let i = 0; i < rings; i++) {
      const r = w * 0.1 + i * w * 0.12 + pulse * w * 0.05;
      ctx.beginPath();
      ctx.arc(w / 2, h / 2, r, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.restore();
  }
}

export function stopRenderLoop(): void {
  cancelAnimationFrame(animFrameId);
}

function drawHoveredNoteTooltip(ctx: CanvasRenderingContext2D, note: HumNote) {
  ctx.save();
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 1.5;
  ctx.globalAlpha = 0.9;
  ctx.beginPath();
  ctx.arc(note.x, note.y, note.size * 0.8, 0, Math.PI * 2);
  ctx.stroke();

  const text = `${Math.round(note.frequency)} Hz${note.emotion ? ` · ${note.emotion}` : ''}`;
  ctx.font = '11px Inter, sans-serif';
  const textWidth = ctx.measureText(text).width;
  const boxW = textWidth + 16;
  const boxH = 22;
  const boxX = note.x - boxW / 2;
  const boxY = note.y - note.size - 28;

  ctx.fillStyle = 'rgba(10, 10, 20, 0.9)';
  ctx.strokeStyle = note.color;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.roundRect(boxX, boxY, boxW, boxH, 6);
  ctx.fill();
  ctx.stroke();

  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, note.x, boxY + boxH / 2);
  ctx.restore();
}

export function getCanvasForExport(): HTMLCanvasElement {
  return canvas;
}

export function notesToSerialized(n: HumNote[]) {
  return n.map(note => ({
    frequency: note.frequency,
    amplitude: note.amplitude,
    shape: note.shape,
    color: note.color,
    x: note.x,
    y: note.y,
    size: note.size,
    rotation: note.rotation,
    timestamp: note.timestamp,
    duration: note.duration,
    emotion: note.emotion,
    style: note.style,
  }));
}
